/**
 * Marca do tenant (spec 017): substitui o "pooltrack" que estava escrito à mão
 * na barra lateral, no portal, no login e nas páginas públicas. Logo quando o
 * tenant tem; senão o nome (fantasia → tenant → produto) com o chevron do
 * produto na cor do tenant.
 */
import { useQuery } from '@tanstack/react-query';
import { ChevronsRight } from 'lucide-react';
import { PRODUCT_BRAND, type Brand, type PublicTenantProfile } from '@erp/shared';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
// logo autenticada: a rota /brand/logo em modo JSON devolve a presignada,
// usada direto como src (o hook é genérico sobre o caminho)
import { useChatMedia } from '@/features/chats/use-chat-media';
import { useBrand } from './tenant-profile';

interface WordmarkProps {
  brand: Brand;
  /** URL pronta para `src` (presignada); null = sem logo */
  logoSrc: string | null;
  className?: string;
  iconClassName?: string;
  logoClassName?: string;
  /** barra recolhida: só o símbolo */
  compact?: boolean;
}

export function BrandWordmark({
  brand,
  logoSrc,
  className,
  iconClassName,
  logoClassName,
  compact,
}: WordmarkProps) {
  const accent = brand.primaryColor ? { color: brand.primaryColor } : undefined;
  const logo = logoSrc ? (
    <img
      src={logoSrc}
      alt=""
      aria-hidden
      className={cn('size-7 shrink-0 rounded-sm bg-white/90 object-contain p-0.5', logoClassName)}
    />
  ) : null;

  if (compact) {
    return (
      <span aria-label={brand.name} title={brand.name} className="flex items-center">
        {logo ?? (
          <ChevronsRight className={cn('size-6 text-primary', iconClassName)} style={accent} strokeWidth={3} />
        )}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex min-w-0 max-w-full select-none items-center gap-2 font-bold leading-none tracking-tight',
        className
      )}
    >
      {logo}
      <span className="truncate">{brand.name}</span>
      {logo ? null : (
        <ChevronsRight
          className={cn('-ml-1.5 size-5 shrink-0 text-primary', iconClassName)}
          style={accent}
          strokeWidth={3}
          aria-hidden
        />
      )}
    </span>
  );
}

/** src da logo do tenant logado: a rota autenticada vira presignada; URL pronta passa direto. */
export function useBrandLogoSrc(brand: Brand): string | null {
  const route = brand.logoUrl?.startsWith('/') ? brand.logoUrl : null;
  const media = useChatMedia(route);
  if (route) return media.url;
  return brand.logoUrl;
}

/** Marca do tenant de quem está logado — equipe e portal. */
export function AppBrandMark(props: Omit<WordmarkProps, 'brand' | 'logoSrc'>) {
  const brand = useBrand();
  const logoSrc = useBrandLogoSrc(brand);
  return <BrandWordmark brand={brand} logoSrc={logoSrc} {...props} />;
}

// ── telas públicas (sem login) ──────────────────────────────────────────────

/**
 * De qual tenant é a página pública, pelo que ela já carrega na URL: o token
 * da avaliação (/rate/<token>), do formulário (/form/<token>), o slug do
 * artigo (/kb/<slug>) ou o `?tenant=` do widget e da base. Sem nenhum, a marca
 * é a do produto.
 */
export function publicBrandRef(pathname: string, search: string): Record<string, string> | null {
  const token = /^\/(rate|form|kb)\/([0-9a-fA-F-]{36})(?:\/|$)/.exec(pathname);
  if (token?.[1] && token[2]) return { [token[1]]: token[2] };
  const tenant = new URLSearchParams(search).get('tenant')?.trim();
  return tenant ? { tenant } : null;
}

/** Perfil público do tenant da página (marca + vocabulário); null enquanto/se não houver. */
export function usePublicTenantProfile(): { profile: PublicTenantProfile | null; pending: boolean } {
  const ref = publicBrandRef(window.location.pathname, window.location.search);
  const q = useQuery({
    queryKey: ['erp', 'public-brand', ref] as const,
    enabled: ref !== null,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const res = await api<PublicTenantProfile>('/public/brand', { query: ref ?? {}, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
  return { profile: q.data ?? null, pending: ref !== null && q.isPending };
}

/**
 * Marca nas telas públicas: a do tenant da página, ou a do produto. Enquanto a
 * do tenant carrega, um espaço vazio da mesma altura — mostrar a do produto e
 * trocar em seguida seria piscar outra marca na cara do cliente.
 */
export function PublicBrandMark(props: Omit<WordmarkProps, 'brand' | 'logoSrc'>) {
  const { profile, pending } = usePublicTenantProfile();
  if (pending) return <span aria-hidden className={cn('block h-7 w-32', props.className)} />;
  const brand = profile?.brand ?? PRODUCT_BRAND;
  return <BrandWordmark brand={brand} logoSrc={brand.logoUrl} {...props} />;
}
